import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FormControl } from "@mui/material";
import { Formik } from "formik";
import { toast, ToastContainer } from 'react-toastify';
import { useUser } from "../../hooks/useUser";
import leftLogo from "../../assets/abbottLogo.png";
import rightLogo from "../../assets/orevan.png";
import orevanEventsLogo from "../../assets/orevanEventsLogo.png";
import eventLogo from "../../assets/loginPage.png";
import "./Login.css";

const AdminLogin = () => {
  const navigate = useNavigate();
  const authContext = useAuth();
  const [error, setError] = useState(false);
  const [errorRegister, setErrorRegister] = useState(false);
  const [BlockedErrorMsg, setBlockedErrorMsg] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSpinning, setShowSpinning] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { data: usersData } = useUser();

  useEffect(() => {
    // If user is already authenticated, redirect to home
    if (authContext.currentUser) {
      navigate('/');
    }
  }, [authContext.currentUser, navigate]);

  if (!authContext) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-[18vw] h-[18vw] bg-gradient-to-tr from-blueColor to-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const { loginWithEmail, error: authError } = authContext;

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const LoginFunc = async (e) => {
    e.preventDefault();
    setShowSpinning(true);
    setError(false);
    setErrorRegister(false);
    setBlockedErrorMsg(false);

    try {
      const emailValue = email.toLowerCase();
      const passwordValue = password;

      if (!emailValue || !passwordValue) {
        setError(true);
        return;
      }

      if (!usersData) {
        toast.error('Failed to fetch user data');
        return;
      }

      const user = usersData.find(user =>
        user.email?.toLowerCase() === emailValue ||
        user.email_address?.toLowerCase() === emailValue
      );

      if (!user) {
        setErrorRegister(true);
        return;
      }

      if (user.condition?.blocked || user.Condition?.Blocked) {
        setBlockedErrorMsg(true);
        return;
      }

      await loginWithEmail(emailValue, passwordValue);

      // Wait a moment to ensure the auth state is updated
      setTimeout(() => {
        if (authContext.currentUser) {
          toast.success('Login successful!');
          navigate('/');
        }
      }, 1000);

    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'auth/wrong-password' ||
        error.code === 'auth/user-not-found' ||
        error.code === 'auth/invalid-email') {
        setError(true);
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setShowSpinning(false);
    }
  };

  return (
    <div className="flex w-full h-screen">
      <ToastContainer />
      <div className="w-full flex items-center justify-center lg:w-1/2">
        <div className="bg-white px-10 py-20 rounded-3xl border-2 border-gray-100 shadow xl:w-3/5 lg:w-3/4 md:w-3/5">
          <div className="flex justify-center">
            <img
              src={orevanEventsLogo}
              alt=""
              className="w-60"
              style={{ objectFit: "contain" }}
            />
          </div>
          <p className="font-medium text-lg text-gray-500 mt-4 text-center">
            Welcome Back! Please enter your admin credentials
          </p>
          <div className="mt-8">
            <Formik>
              {() => (
                <form onSubmit={LoginFunc}>
                  <FormControl className="w-100">
                    <span className="text-danger">
                      {error && "Invalid Email or Password"}
                      {errorRegister && "This Email Not Registered"}
                      {authError && authError}
                    </span>
                  </FormControl>
                  <div>
                    <label className="text-lg font-medium my-3" htmlFor="email">
                      Email
                    </label>
                    <input
                      name="email"
                      id="email"
                      className="w-full border border-gray-100 p-3 bg-transparent"
                      type="email"
                      placeholder="Enter Your Email.."
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="my-5">
                    <label className="text-lg font-medium my-3" htmlFor="password">
                      Password
                    </label>
                    <div className="relative w-full">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        id="password"
                        placeholder="Enter Your Password.."
                        className="w-full border border-gray-100 p-3 bg-transparent"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <i
                        className={`fa-regular ${showPassword ? "fa-eye" : "fa-eye-slash"
                          } absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer`}
                        onClick={togglePassword}
                      ></i>
                    </div>
                  </div>
                  <div className="mt-8">
                    <a
                      className="text-blueColor"
                      href="mailto:orevanevents@orevan.org"
                    >
                      Contact us
                    </a>
                  </div>
                  <div className="text-danger p-2">
                    {BlockedErrorMsg && "This Account Blocked from The System"}
                  </div>
                  <div className="mt-8">
                    <button
                      type="submit"
                      className="active:scale-[.98] active:duration-75 hover:scale-[1.01] ease-in-out transition-all py-3 rounded-xl bg-gradient-to-tr from-purpleColor to-lightPurple text-white text-lg font-bold w-100 flex justify-center items-center gap-3 w-full"
                    >
                      <span>Sign In</span>
                      {showSpinning && (
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="mt-4 text-center">
                    <Link to="/login" className="text-blueColor hover:underline">
                      Back to Abbott Login
                    </Link>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      <div className="hidden relative lg:flex h-full w-1/2 items-center justify-center flex-col bg-gray-100">
        <div className="mt-[-3vw] mr-[15vw]">
          <div className="w-[18vw] h-[18vw] bg-gradient-to-tr from-blueColor to-blue-500 rounded-full animate-spin"></div>
        </div>
        <div className="mt-[-12vw] z-10">
          <img className="w-[20vw]" src={eventLogo} alt="" />
        </div>
        <div className="mt-[-10vw] ml-[8vw]">
          <div className="w-[18vw] h-[18vw] bg-gradient-to-tr from-purpleColor to-lightPurple rounded-full animate-spin"></div>
        </div>

        <div className="w-full absolute bottom-0 bg-white/10 backdrop-blur-lg flex justify-end flex-col">
          <div className="grid lg:grid-cols-4 xl:grid-cols-6 gap-x-32 lg:px-4 xl:px-10 mb-4 items-center">
            <div className="lg:col-span-2 xl:col-span-3 mt-3">
              <img className="w-56" src={leftLogo} alt="" />
            </div>
            <div className="lg:col-span-2 xl:col-span-3 flex justify-end">
              <img
                src={rightLogo}
                className="w-44"
                alt=""
                style={{ objectFit: "contain" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 